import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  formatRut,
  isValidRut,
  isValidSerialNumber,
  formatSerialNumber
} from "../../../utils/input/rut";
import isValidEmail from "../../../utils/input/email";
import formatCustomInputValue from "../../../utils/input/custom";
import { acceptedInputTypes, TYPES as _TYPES } from "../../../utils/data/types";

import "./style.css";

const ERROR_TYPES = {
  REQUIRED: "REQUIRED",
  FORMAT: "FORMAT"
};

const TYPES = _TYPES();
/* istanbul ignore next */
const Input = props => {
  const {
    name,
    type,
    label,
    value,
    required,
    placeholder,
    minLength,
    maxLength,
    autocomplete,
    disabled,
    readonly,
    errorMessage,
    errorIcon,
    hintText,
    tooltip,
    state,
    focused,
    liveValidation,
    allowedCharacters,
    notAllowedCharacters,
    loading,
    onChange,
    onFocus,
    onBlur,
    onCopy,
    onDrag,
    onDrop,
    onPaste
  } = props;

  const [internalValue, setInternalValue] = useState(value);
  const [eventInfo, setEventInfo] = useState(null);
  const input = useRef(null);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (!eventInfo) return;

    if (eventInfo) onChange(_generateExitEvent(eventInfo, liveValidation));
  }, [eventInfo]);

  useEffect(() => {
    if (focused) input.current.focus();
  }, [focused]);

  const handleBlur = eventInfo => {
    onBlur(_generateExitEvent(eventInfo, false));
  };

  const handleChangeInput = e => {
    const _maxLength = +maxLength || acceptedInputTypes[type].max;

    if (e.target.value.length > _maxLength)
      e.target.value = e.target.value.slice(0, _maxLength);

    e.persist();
    setEventInfo(e);

    setInternalValue(_formattedValue(e.target, type));
  };

  const _formattedValue = (_target, _type) => {
    const unmaskedValue = _target.value;

    switch (_type) {
      case TYPES.rut:
        return formatRut(unmaskedValue);
      case TYPES.serial_number:
        return formatSerialNumber(unmaskedValue);
      case TYPES.custom:
        return formatCustomInputValue(unmaskedValue, allowedCharacters, notAllowedCharacters)
      default:
        return _target.value;
    }
  };

  const _generateExitEvent = (eventInfo, isLiveValidation = false) => {
    const result = {
      target: {
        value: _unmaskedValue(eventInfo.target.value, type),
        masked: eventInfo.target.value
      }
    };
    let errors = [];

    if (required && !result.target.value)
      errors = errors.concat([{ type: ERROR_TYPES.REQUIRED }]);

    errors = errors.concat(
      _generateFormatErrors(result.target.value, type, isLiveValidation)
    );
    return errors.length ? { ...result, errors } : { ...result };
  };

  const _unmaskedValue = (value, type) => {
    switch (type) {
      case TYPES.rut:
        return value.replace(/[-.]/g, "");
      case TYPES.serial_number:
        return value.replace(/[-.]/g, "");
      default:
        return value;
    }
  };

  const _generateFormatErrors = (value, type, isLiveValidation) => {
    let errors = [];

    switch (type) {
      case TYPES.rut:
        if (!isValidRut(value)) errors = [{ type: ERROR_TYPES.FORMAT }];
        break;
      case TYPES.serial_number:
        if (!isValidSerialNumber(value, isLiveValidation))
          errors = [{ type: ERROR_TYPES.FORMAT }];
        break;
      case TYPES.email:
        if (!isValidEmail(value)) errors = [{ type: ERROR_TYPES.FORMAT }];
        break;
      default:
        return [];
    }
    return errors;
  };

  return (
    <article
      className={`Input Input--${state}${disabled ? " Input--disabled" : ""}${
        focused ? " Input--focused" : ""
      }${loading ? " Input--loading" : ""}`}
    >
      <div className="Input__label-wrapper">
        <label
          className={`${loading ? "Input--animated-background" : ""}`}
          htmlFor={name}
        >
          {label}
        </label>
        {tooltip}
      </div>

      <section className="Input__input-wrapper">
        <div
          className={`Input__input ${
            type === "phone" ? "Input__input-phone" : ""
          }`}
        >
          <input
            className={loading ? "Input--animated-background" : ""}
            ref={input}
            name={name}
            type={acceptedInputTypes[type].htmlType}
            value={internalValue}
            required={required}
            placeholder={placeholder}
            minLength={+minLength}
            maxLength={+maxLength || acceptedInputTypes[type].max}
            autoComplete={acceptedInputTypes[type].autoComplete || autocomplete}
            disabled={disabled || loading}
            readOnly={readonly}
            onChange={handleChangeInput}
            onFocus={onFocus}
            onBlur={handleBlur}
            onCopy={onCopy}
            onDrag={onDrag}
            onDrop={onDrop}
            onPaste={onPaste}
          />
        </div>
        {hintText && (
          <div className="Input__hint-text">
            <small className={loading ? "Input--animated-background" : ""}>
              {hintText}
            </small>
          </div>
        )}
        {errorMessage && (
          <div className="Input__message">
            <small className={loading ? "Input--animated-background" : ""}>
              {errorIcon}
              {errorMessage}
            </small>
          </div>
        )}
      </section>
    </article>
  );
};

Input.defaultProps = {
  label: "",
  value: "",
  required: false,
  placeholder: "",
  minLength: "1",
  maxLength: "100",
  autocomplete: "on",
  disabled: false,
  readonly: false,
  errorMessage: "",
  errorIcon: React.createElement('img'),
  hintText: "",
  state: "normal",
  focused: false,
  liveValidation: false,
  allowedCharacters: {},
  notAllowedCharacters: {},
  tooltip: null, 
  loading: false,
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onCopy: () => {},
  onDrag: () => {},
  onDrop: () => {},
  onPaste: () => {}
};

Input.propTypes = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.keys(TYPES)).isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  minLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autocomplete: PropTypes.oneOf(["on", "off"]),
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  errorMessage: PropTypes.string,
  errorIcon: PropTypes.element,
  hintText: PropTypes.string,
  state: PropTypes.oneOf(["normal", "error", "success", "warning"]),
  focused: PropTypes.bool,
  liveValidation: PropTypes.bool,
  allowedCharacters: PropTypes.object,
  notAllowedCharacters: PropTypes.object,
  tooltip: PropTypes.element,
  loading: PropTypes.bool,  
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onCopy: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onDrag: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onDrop: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  onPaste: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
};

export default Input;
